import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import CabiBubble from "../../components/Kids/CabiBubble";
import Button from "../../components/Kids/Button";
import useGameSounds from "../../hooks/useGameSounds";
import useKidVoice from "../../hooks/useKidVoice";
import { getCaptchaToken } from "../../components/ReCaptcha";
import Seo from "../../components/Seo";
import SEO from "../../seo/pages.json";

const API = process.env.REACT_APP_API_URL;

export default function ParentLogin() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const sounds      = useGameSounds();
  const { speak }   = useKidVoice();

  const [form, setForm]       = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [needsVerify, setNeedsVerify] = useState(false);
  const [resending, setResending]     = useState(false);

  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const resendVerification = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/api/kids/parent/resend-verification`, { email: form.email });
      toast.success("Verification email resent — check your inbox (and spam folder)!");
    } catch (err) {
      toast.error(err.response?.data?.message || "Couldn't resend the email. Try again shortly.");
    } finally {
      setResending(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNeedsVerify(false);
    try {
      const res = await axios.post(`${API}/api/kids/parent/login`, {
        email: form.email,
        password: form.password,
      });
      const token  = res.data.data?.token;
      const user   = res.data.data?.user;
      const parent = res.data.data?.parent;
      const subscription = res.data.data?.subscription;

      localStorage.setItem("kids_parent_token", token);
      localStorage.setItem("kids_parent_user", JSON.stringify(user));
      localStorage.setItem("kids_active_mode", "parent");

      // Cache a minimal family list so kids can log in with just their PIN on this device
      const children = (parent?.children || []).map(c => ({
        id: c.id, name: c.name, avatar: c.avatar, has_pin: c.has_pin,
      }));
      localStorage.setItem("kids_family", JSON.stringify({ email: form.email, children }));

      sounds.playComplete(false);
      speak("Welcome back to Cabil Kids!");

      if (subscription?.is_expired) {
        toast.info("Your trial has ended. Choose a plan to keep learning! 🌟");
        navigate("/kids/parent/settings");
      } else {
        toast.success("Welcome back! 🌟");
        navigate("/kids/parent/dashboard");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Invalid email or password.");
      if (err.response?.status === 403) setNeedsVerify(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-4 font-body"
      style={{ background: "linear-gradient(150deg,#FFF6EE,#FFF9F4 55%,#fff)" }}
    >
      <Seo
        {...SEO["/kids/login"]}
        path="/kids/login"
      />
      <style>{`
        @keyframes slideUp { from{transform:translateY(20px);opacity:0} to{transform:translateY(0);opacity:1} }
        .slide-up { animation: slideUp 0.4s ease both; }
      `}</style>

      {/* Logo */}
      <div className="mb-6 text-center">
        <Link to="/kids" className="font-display font-semibold text-2xl tracking-tight">
          <span className="text-brand">Cabil</span> <span className="text-gold">Kids</span>
        </Link>
      </div>

      <div className="w-full max-w-md">
        {/* Cabi mascot tip */}
        <CabiBubble
          mood="waving"
          theme="light"
          size={72}
          className="mb-5 px-2"
          text="Welcome back! Sign in to pick up where your child left off. 🚀"
        />

        <div className="bg-white rounded-sheet shadow-sheet p-8 slide-up">
          <h1 className="font-display font-semibold text-2xl text-ink mb-1">{t("kids.login.title")}</h1>
          <p className="text-muted text-sm mb-6">{t("kids.login.subtitle")}</p>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block font-body font-extrabold text-[13px] text-ink mb-1.5">{t("kids.login.email")}</label>
              <input
                type="email" name="email" value={form.email} onChange={change} required
                className="w-full border border-[#E5DED4] rounded-xl px-4 py-3 text-ink font-body font-semibold text-[15px] focus:ring-2 focus:ring-brand/30 focus:border-brand outline-none transition"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block font-body font-extrabold text-[13px] text-ink">{t("kids.login.password")}</label>
                <Link to="/kids/forgot-password" className="font-body font-extrabold text-[12.5px] text-brand hover:underline">
                  Forgot password?
                </Link>
              </div>
              <input
                type="password" name="password" value={form.password} onChange={change} required
                className="w-full border border-[#E5DED4] rounded-xl px-4 py-3 text-ink font-body font-semibold text-[15px] focus:ring-2 focus:ring-brand/30 focus:border-brand outline-none transition"
              />
            </div>
            <Button type="submit" disabled={loading} className="w-full">
              {loading ? "Signing in…" : `${t("kids.login.login")} 🚀`}
            </Button>
          </form>

          {needsVerify && (
            <button onClick={resendVerification} disabled={resending}
              className="w-full mt-3 text-center text-brand font-body font-extrabold text-sm hover:underline transition disabled:opacity-60">
              {resending ? "Resending…" : "Resend verification email"}
            </button>
          )}

          <p className="text-center text-sm text-muted font-body font-semibold mt-4">
            {t("kids.login.noAccount")}{" "}
            <Link to="/kids/signup" className="text-brand font-extrabold">{t("kids.login.signup")}</Link>
          </p>
        </div>

        {/* Kid login switch */}
        <Link to="/kids/kid-login"
          className="mt-4 flex items-center justify-center gap-2 bg-white border-2 border-cardline hover:border-brand rounded-card py-3.5 text-ink font-body font-extrabold text-sm transition shadow-card">
          <span className="text-xl">🧒</span> {t("kids.login.kidLogin")}
        </Link>
      </div>
    </div>
  );
}
